/**
 * PWA Branding Initializer
 * Handles runtime PWA configuration for multi-tenant support
 * Following expert recommendations for iOS compatibility
 */

/**
 * Helper to ensure a meta tag exists with given attributes
 */
function ensureMetaTag(name, content, property = 'name') {
  let meta = document.querySelector(`meta[${property}="${name}"]`);
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(property, name);
    document.head.appendChild(meta);
  }
  
  meta.content = content;
  return meta;
}

/**
 * Helper to ensure a link tag exists with given attributes
 */
function ensureLinkTag(rel, attributes) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  
  // Set all attributes
  Object.entries(attributes).forEach(([key, value]) => {
    link.setAttribute(key, value);
  });
  
  return link;
}

/**
 * Get tenant assets base URL with version
 */
function getTenantAssetsUrl(config) {
  const tenantId = config.id || 'default';
  const version = config.version || Date.now();
  return `/tenant-assets/${tenantId}`;
}

/**
 * Initialize PWA branding based on config
 * This runs synchronously after config is loaded but before React renders
 */
export function initializeBranding(config) {
  if (!config) {
    console.error('initializeBranding: No config provided');
    return;
  }

  console.log('Initializing PWA branding for:', config.name);
  console.log('[PWA] Full config:', {
    name: config.name,
    short_name: config.short_name,
    id: config.id,
    pwa_icons: config.pwa_icons ? 'Present' : 'Missing',
    pwa_manifest_url: config.pwa_manifest_url || 'Not set'
  });
  
  const baseUrl = getTenantAssetsUrl(config);
  const version = config.version || Date.now();
  
  // Send tenant config to service worker
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SET_TENANT_CONFIG',
      tenantId: config.id || 'default',
      version: version,
      config: config // Send full config for dynamic manifest generation
    });
  }
  
  // Also send to any new service workers that might be installing
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      if (registration.active) {
        registration.active.postMessage({
          type: 'SET_TENANT_CONFIG',
          tenantId: config.id || 'default',
          version: version,
          config: config // Send full config for dynamic manifest generation
        });
      }
    });
  }
  
  // 1. iOS-specific tags (MUST be real URLs, not blob/data URLs)
  // Check if config has PWA icons from Supabase
  if (config.pwa_icons && config.pwa_icons['apple-touch-icon.png']) {
    // Use Supabase-hosted icons
    ensureLinkTag('apple-touch-icon', {
      href: config.pwa_icons['apple-touch-icon.png'],
      sizes: '180x180'
    });
    
    // Additional Apple touch icon sizes
    [120, 152, 167].forEach(size => {
      const iconKey = `icon-${size}.png`;
      if (config.pwa_icons[iconKey]) {
        ensureLinkTag('apple-touch-icon', {
          href: config.pwa_icons[iconKey],
          sizes: `${size}x${size}`
        });
      }
    });
  } else {
    // Fallback to local tenant assets
    ensureLinkTag('apple-touch-icon', {
      href: `${baseUrl}/apple-touch-icon.png?v=${version}`,
      sizes: '180x180'
    });
    
    // Additional Apple touch icon sizes
    [120, 152, 167].forEach(size => {
      ensureLinkTag('apple-touch-icon', {
        href: `${baseUrl}/icon-${size}.png?v=${version}`,
        sizes: `${size}x${size}`
      });
    });
  }
  
  // Apple mobile web app title - This is what iOS uses for the app name
  ensureMetaTag('apple-mobile-web-app-title', config.short_name || config.name);
  
  // Apple mobile web app capable
  ensureMetaTag('apple-mobile-web-app-capable', 'yes');
  
  // Apple status bar style
  ensureMetaTag('apple-mobile-web-app-status-bar-style', 'default');
  
  // 2. Android/Chrome manifest
  // IMPORTANT: Manifest must be at root path for PWA to work properly
  // The service worker will intercept and serve tenant-specific content
  // Add timestamp to force refresh
  const manifestUrl = config.pwa_manifest_url 
    ? `/manifest.json?v=${version}&t=${Date.now()}`
    : `/manifest.json?v=${version}&t=${Date.now()}`;
  
  ensureLinkTag('manifest', {
    href: manifestUrl
  });
  
  // Log manifest URL for debugging
  console.log('[PWA] Manifest URL:', manifestUrl);
  if (config.pwa_manifest_url) {
    console.log('[PWA] Using Supabase manifest:', config.pwa_manifest_url);
  }
  
  // 3. Theme color (used by both iOS and Android)
  ensureMetaTag('theme-color', config.colors?.primary || config.primaryColor || '#000000');
  
  // 4. General meta tags
  ensureMetaTag('application-name', config.name);
  
  // 5. MS Tile tags (for Windows)
  ensureMetaTag('msapplication-TileColor', config.colors?.primary || config.primaryColor || '#000000');
  ensureMetaTag('msapplication-TileImage', `${baseUrl}/icon-144.png?v=${version}`, 'name');
  
  // 6. Favicon
  ensureLinkTag('icon', {
    type: 'image/png',
    sizes: '32x32',
    href: `${baseUrl}/icon-32.png?v=${version}`
  });
  
  ensureLinkTag('icon', {
    type: 'image/png',
    sizes: '16x16',
    href: `${baseUrl}/icon-16.png?v=${version}`
  });
  
  // 7. Update page title
  document.title = config.name || 'App';
  
  // 8. Open Graph tags (for social sharing)
  ensureMetaTag('og:title', config.name, 'property');
  ensureMetaTag('og:site_name', config.name, 'property');
  ensureMetaTag('og:image', `${baseUrl}/icon-512.png?v=${version}`, 'property');
  
  console.log('PWA branding initialized successfully');
}

/**
 * Force refresh of PWA assets (for updates)
 * Note: On iOS, this won't update already installed PWAs
 */
export function refreshPWAAssets(config) {
  // Update version to force cache refresh
  const newConfig = {
    ...config,
    version: Date.now()
  };
  
  initializeBranding(newConfig);
  
  // For iOS users who have already installed, show update prompt
  if (isIOSDevice() && isStandalone()) {
    showIOSUpdatePrompt(config);
  }
}

/**
 * Check if device is iOS
 */
export function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Check if app is running in standalone mode (installed PWA)
 */
export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}

/**
 * Show update prompt for iOS users
 */
function showIOSUpdatePrompt(config) {
  // Only show if we haven't shown recently
  const lastPrompt = localStorage.getItem('pwa-update-prompt');
  const now = Date.now();
  
  if (lastPrompt && (now - parseInt(lastPrompt)) < 86400000) { // 24 hours
    return;
  }
  
  localStorage.setItem('pwa-update-prompt', now.toString());
  
  // Create a custom prompt (you can style this better)
  const message = `${config.name} has been updated. To see the new icon and name, please delete this app from your home screen and add it again.`;
  
  // You can implement a better UI prompt here
  if (window.confirm(message)) {
    // Optionally provide instructions
    alert('To update:\n1. Press and hold the app icon\n2. Tap "Remove from Home Screen"\n3. Open Safari and visit this site\n4. Tap Share > Add to Home Screen');
  }
}

/**
 * Generate assets path with proper cache busting
 */
export function getAssetPath(config, assetName) {
  const baseUrl = getTenantAssetsUrl(config);
  const version = config.version || Date.now();
  return `${baseUrl}/${assetName}?v=${version}`;
}

/**
 * Preload critical PWA assets
 */
export function preloadPWAAssets(config) {
  const baseUrl = getTenantAssetsUrl(config);
  const version = config.version || Date.now();
  
  // Preload critical icons
  const criticalAssets = [
    'apple-touch-icon.png',
    'icon-192.png',
    'icon-512.png',
    'manifest.json'
  ];
  
  criticalAssets.forEach(asset => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = asset.endsWith('.json') ? 'fetch' : 'image';
    link.href = `${baseUrl}/${asset}?v=${version}`;
    document.head.appendChild(link);
  });
}